import { useState, useEffect, useRef, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface } from "@/components/common";
import { useLocale } from "@/context/LocaleContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadCloud, Brain, CheckCircle2, Lightbulb, MoreHorizontal, ExternalLink,
  Pencil, FolderInput, Trash2, FileText, Package, MessageSquare, TrendingUp, Users, Loader2, PenLine, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const CATEGORY_META = {
  brand: { label: "Brand", icon: FileText, items: ["Mission", "Vision", "Værdier", "Tone of voice", "Målgrupper", "Positionering"] },
  products: { label: "Produkter & ydelser", icon: Package, items: ["Produktark", "Datablade", "Brochurer", "Cases", "Prislister"] },
  communication: { label: "Kommunikation", icon: MessageSquare, items: ["LinkedIn-opslag", "Pressemeddelelser", "Nyhedsbreve", "Artikler", "Blogindlæg"] },
  sales: { label: "Salg & marketing", icon: TrendingUp, items: ["PowerPoints", "Salgspræsentationer", "Whitepapers", "Kundecases", "Kampagnemateriale"] },
  organization: { label: "Organisation", icon: Users, items: ["Ledelsesprofiler", "Medarbejderprofiler", "Ekspertområder", "CV'er"] },
};

function fmtDate(dt) { try { return new Date(dt).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } }

const TONES = ["Professionel", "Personlig og varm", "Ekspert og faglig", "Uformel og legende", "Visionær og inspirerende", "Kort og præcis"];
const INDUSTRIES = ["Byggeri & arkitektur", "IT & software", "Produktion & industri", "Detail & e-handel", "Sundhed & pharma", "Finans & jura", "Energi & miljø", "Fødevarer", "Rådgivning & service", "Andet"];

export default function Knowledge() {
  const { t } = useLocale();
  const { company, refresh } = useAuth();
  const [docs, setDocs] = useState(null);
  const [kn, setKn] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [profile, setProfile] = useState({ industry: "", tone: "", linkedin: "", description: "", audience: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [moveDoc, setMoveDoc] = useState(null);
  const [moveVal, setMoveVal] = useState("products");
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteView, setNoteView] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [d, k] = await Promise.all([api.get("/documents"), api.get("/knowledge")]);
      setDocs(d.data.documents);
      setKn(k.data);
    } catch (err) { toast.error(apiError(err)); setDocs([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (company) setProfile({
      industry: company.industry || "", tone: company.tone || "", linkedin: company.linkedin || "",
      description: company.description || "", audience: company.audience || "",
    });
  }, [company]);

  const saveProfile = async () => {
    if (!company) return;
    setSavingProfile(true);
    try {
      await api.put("/company", { ...company, ...profile });
      await refresh();
      await load();
      toast.success(t("Profil opdateret"));
    } catch (err) { toast.error(apiError(err)); } finally { setSavingProfile(false); }
  };

  // Auto-poll while any document is still being analyzed (background OCR)
  useEffect(() => {
    if (docs && docs.some((d) => (d.status || "").startsWith("Analyserer"))) {
      const timer = setTimeout(load, 4000);
      return () => clearTimeout(timer);
    }
  }, [docs, load]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let last = null;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        last = data.knowledge;
        toast.success(`"${file.name}" uploadet og analyseret`);
      } catch (err) { toast.error(apiError(err, `Kunne ikke uploade ${file.name}`)); }
    }
    if (last) setKn(last);
    await load();
    setUploading(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); };

  const openDoc = async (doc) => {
    if (doc.is_note || !doc.storage_path) { setNoteView(doc); return; }
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveNote = async () => {
    if (!noteContent.trim()) { toast.error(t("Skriv noget tekst om virksomheden")); return; }
    setSavingNote(true);
    try {
      const { data } = await api.post("/documents/note", { title: noteTitle, content: noteContent });
      if (data.knowledge) setKn(data.knowledge);
      setNoteTitle(""); setNoteContent("");
      toast.success(t("Tekst gemt i vidensbanken"));
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setSavingNote(false); }
  };

  const doRename = async () => {
    try { await api.put(`/documents/${renameDoc.id}`, { original_filename: renameVal }); toast.success(t("Omdøbt")); setRenameDoc(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const doMove = async () => {
    try { await api.put(`/documents/${moveDoc.id}`, { category: moveVal }); toast.success(t("Flyttet")); setMoveDoc(null); load(); if (kn) load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const doDelete = async (doc) => {
    try { await api.delete(`/documents/${doc.id}`); toast.success(t("Slettet")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const counts = kn?.counts || {};

  return (
    <div>
      <PageHeader overline="Vidensbank" title="Virksomhedens centrale viden"
        subtitle="Jo mere materiale du uploader, desto bedre bliver Aicom AI til at forstå virksomheden og skrive i dens stil." />

      {/* Hero + knowledge card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Surface className="lg:col-span-2" testid="knowledge-hero">
          <h2 className="font-display text-xl font-medium">{t("Gør Aicom AI endnu klogere på din virksomhed")}</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {t("Upload dokumenter, cases, præsentationer, produktark, pressemeddelelser, nyhedsbreve, brochurer og andet materiale. Aicom AI analyserer indholdet og bruger det aktivt til at skabe mere relevante og virksomhedsspecifikke kommunikationsforslag.")}
          </p>
        </Surface>

        <Surface testid="knowledge-score">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <Brain className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.15em]">{t("Virksomhedskendskab")}</span>
          </div>
          <p className="font-display text-4xl font-medium" data-testid="knowledge-percent">{kn ? `${kn.percent} %` : "…"}</p>
          <Progress value={kn?.percent || 0} className="h-1.5 mt-3" />
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            {t("Jo mere materiale du uploader, desto bedre bliver Aicom AI til at forstå din virksomhed og levere relevante forslag.")}
          </p>
        </Surface>
      </div>

      {/* Company profile — make the AI smarter */}
      <Surface className="mb-6" testid="knowledge-profile">
        <div className="flex items-center gap-2 mb-1">
          <Brain className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-lg font-medium">{t("Gør Aicom AI klogere med jeres profil")}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">{t("Angiv branche, tone of voice, LinkedIn og mere — så skriver Aicom AI endnu mere præcist i jeres stil.")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("Branche")}</Label>
            <Select value={profile.industry} onValueChange={(v) => setProfile({ ...profile, industry: v })}>
              <SelectTrigger data-testid="profile-industry"><SelectValue placeholder={t("Vælg branche")} /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("Ønsket tone of voice")}</Label>
            <Select value={profile.tone} onValueChange={(v) => setProfile({ ...profile, tone: v })}>
              <SelectTrigger data-testid="profile-tone"><SelectValue placeholder={t("Vælg tone")} /></SelectTrigger>
              <SelectContent>{TONES.map((tn) => <SelectItem key={tn} value={tn}>{tn}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("Primær målgruppe")}</Label>
            <Input value={profile.audience} onChange={(e) => setProfile({ ...profile, audience: e.target.value })} data-testid="profile-audience" placeholder={t("Fx arkitekter og bygherrer")} />
          </div>
          <div className="space-y-2">
            <Label>{t("LinkedIn-side eller -profil")}</Label>
            <Input value={profile.linkedin} onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })} data-testid="profile-linkedin" placeholder="https://linkedin.com/company/..." />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>{t("Kort beskrivelse af virksomheden")}</Label>
            <Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={3} data-testid="profile-description" placeholder={t("Hvad laver I, og hvad gør jer særlige?")} />
          </div>
        </div>
        <Button className="mt-4 gap-2" onClick={saveProfile} disabled={savingProfile || !company} data-testid="profile-save">
          {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("Gem profil")}
        </Button>
      </Surface>

      {/* Sensitive-data warning + required confirmation */}
      <p className="flex items-start gap-2 text-xs text-muted-foreground mb-3" data-testid="knowledge-sensitive-warning">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.75} />
        {t("Undlad at uploade følsomme personoplysninger (fx helbred, CPR-numre, race eller religion). Du er selv dataansvarlig for det, du uploader. Indhold behandles af vores AI-leverandør OpenAI og kan opbevares dér i op til 30 dage. Det bruges kun til at levere jeres egne resultater – aldrig til at træne tredjeparters AI-modeller.")}
      </p>
      <label className="flex items-start gap-2.5 text-sm text-foreground mb-4 cursor-pointer select-none" data-testid="knowledge-confirm-label">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
          data-testid="knowledge-confirm-checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-foreground cursor-pointer" />
        <span>{t("Jeg bekræfter, at det uploadede materiale ikke indeholder personfølsomme oplysninger.")}</span>
      </label>

      {/* Upload box */}
      <div
        onDragOver={(e) => { if (!confirmed) return; e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { if (!confirmed) { e.preventDefault(); return; } onDrop(e); }}
        onClick={() => { if (confirmed) fileRef.current?.click(); }}
        data-testid="knowledge-dropzone"
        aria-disabled={!confirmed}
        className={`rounded-xl border border-dashed p-10 text-center transition-colors duration-200 mb-6 ${!confirmed ? "opacity-50 cursor-not-allowed border-border" : `cursor-pointer ${dragging ? "border-foreground bg-secondary" : "border-border hover:border-muted-foreground/50"}`}`}
      >
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="knowledge-file-input"
          onChange={(e) => uploadFiles(e.target.files)} />
        {uploading ? (
          <div className="flex items-center justify-center gap-3 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> {t("Uploader og analyserer…")}</div>
        ) : (
          <>
            <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
            <p className="font-display text-lg">{t("Slip filer her eller vælg filer")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("PDF • Word • PowerPoint • Excel • Billeder • Tekstfiler")}</p>
            <Button className="mt-4" size="sm" data-testid="knowledge-upload-btn" disabled={!confirmed} onClick={(e) => { e.stopPropagation(); if (confirmed) fileRef.current?.click(); }}>{t("Upload dokument")}</Button>
          </>
        )}
      </div>

      {/* Write text about the company */}
      <Surface className="mb-6" testid="knowledge-note">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-lg font-medium">{t("Eller skriv om virksomheden")}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t("Har du ikke et dokument? Skriv bare fri tekst — fx om jeres historie, produkter, kunder eller værdier. Aicom AI bruger det aktivt.")}</p>
        <Input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder={t("Titel (valgfri) — fx 'Vores historie'")} className="mb-3" data-testid="knowledge-note-title" />
        <Textarea value={noteContent} onChange={(e) => setNoteContent(e.target.value)} rows={5}
          placeholder={t("Skriv tekst om virksomheden her...")} data-testid="knowledge-note-content" />
        <Button className="mt-3 gap-2" onClick={saveNote} disabled={savingNote} data-testid="knowledge-note-save">
          {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} {t("Gem tekst")}
        </Button>
      </Surface>

      {/* Knowledge categories */}
      <h2 className="font-display text-lg font-medium mb-4">{t("Videnskategorier")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="knowledge-categories">
        {Object.entries(CATEGORY_META).map(([key, meta], i) => {
          const isBrand = key === "brand";
          const status = isBrand
            ? t("{n} af 6", { n: kn?.brand_filled ?? 0 })
            : key === "organization" ? t("{n} profiler", { n: counts[key] || 0 }) : t("{n} dokumenter", { n: counts[key] || 0 });
          return (
            <Surface key={key} delay={i * 0.05} testid={`knowledge-cat-${key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><meta.icon className="h-4 w-4 text-muted-foreground" /><h3 className="font-display font-medium">{t(meta.label)}</h3></div>
                <Badge variant="secondary" className="gap-1 text-xs">
                  {isBrand && (kn?.brand_filled === 6) && <CheckCircle2 className="h-3 w-3" />} {status}
                </Badge>
              </div>
              <ul className="space-y-1.5">
                {meta.items.map((it) => <li key={it} className="text-sm text-muted-foreground flex gap-2"><span className="text-foreground/40">·</span>{it}</li>)}
              </ul>
            </Surface>
          );
        })}
      </div>

      {/* AI recommendations */}
      {kn?.recommendations?.length > 0 && (
        <Surface className="mb-6" testid="knowledge-recommendations">
          <div className="flex items-center gap-2 mb-4"><Lightbulb className="h-4 w-4 text-muted-foreground" /><h2 className="font-display text-lg font-medium">{t("Aicom AI anbefaler")}</h2></div>
          <div className="space-y-2">
            {kn.recommendations.map((r, i) => (
              <div key={i} className="flex gap-3 text-sm text-muted-foreground p-3 rounded-lg bg-secondary/50">
                <Lightbulb className="h-4 w-4 text-foreground shrink-0 mt-0.5" /> {r}
              </div>
            ))}
          </div>
        </Surface>
      )}

      {/* Recent documents */}
      <h2 className="font-display text-lg font-medium mb-4">{t("Seneste dokumenter")}</h2>
      {docs === null ? <Spinner label="Henter dokumenter…" /> : docs.length === 0 ? (
        <Surface testid="knowledge-empty"><p className="text-sm text-muted-foreground text-center py-6">{t("Ingen dokumenter endnu. Upload jeres materiale ovenfor.")}</p></Surface>
      ) : (
        <Surface className="p-0 overflow-hidden" testid="knowledge-docs-table">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("Dokument")}</TableHead><TableHead>{t("Kategori")}</TableHead><TableHead>{t("Uploadet")}</TableHead><TableHead>{t("Status")}</TableHead><TableHead className="text-right">{t("Handlinger")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`knowledge-doc-${d.id}`}>
                    <TableCell className="font-medium max-w-[220px] truncate">{d.original_filename}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{t(CATEGORY_META[d.category]?.label || d.category)}</Badge></TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">{fmtDate(d.created_at)}</TableCell>
                    <TableCell>
                      {d.status?.startsWith("Analyserer") ? (
                        <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> {d.status}</span>
                      ) : (
                        <span className={`text-xs ${d.status?.startsWith("Analyseret") ? "text-foreground" : "text-muted-foreground"}`}>{d.status}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`knowledge-actions-${d.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDoc(d)} data-testid={`knowledge-open-${d.id}`}><ExternalLink className="h-4 w-4 mr-2" /> Åbn</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setRenameDoc(d); setRenameVal(d.original_filename); }} data-testid={`knowledge-rename-${d.id}`}><Pencil className="h-4 w-4 mr-2" /> Omdøb</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setMoveDoc(d); setMoveVal(d.category); }} data-testid={`knowledge-move-${d.id}`}><FolderInput className="h-4 w-4 mr-2" /> Flyt kategori</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => doDelete(d)} className="text-destructive" data-testid={`knowledge-delete-${d.id}`}><Trash2 className="h-4 w-4 mr-2" /> Slet</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Surface>
      )}

      {/* Bottom info */}
      <Surface className="mt-6" testid="knowledge-footer-info">
        <h2 className="font-display text-lg font-medium">{t("Alt bliver brugt aktivt")}</h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          {t("Aicom AI bruger automatisk materialet i Vidensbanken, når platformen udvikler kommunikationsforslag, LinkedIn-opslag, nyhedsbreve, pressehistorier og kampagner. Derfor bliver forslagene mere præcise, mere relevante og mere tro mod virksomhedens identitet, jo mere viden AI'en får adgang til.")}
        </p>
      </Surface>

      {/* Rename dialog */}
      <Dialog open={!!renameDoc} onOpenChange={(o) => !o && setRenameDoc(null)}>
        <DialogContent data-testid="knowledge-rename-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("Omdøb dokument")}</DialogTitle></DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid="knowledge-rename-input" />
          <DialogFooter><Button onClick={doRename} data-testid="knowledge-rename-save">{t("Gem")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      <Dialog open={!!moveDoc} onOpenChange={(o) => !o && setMoveDoc(null)}>
        <DialogContent data-testid="knowledge-move-dialog">
          <DialogHeader><DialogTitle className="font-display">Flyt til kategori</DialogTitle></DialogHeader>
          <Select value={moveVal} onValueChange={setMoveVal}>
            <SelectTrigger data-testid="knowledge-move-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(CATEGORY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button onClick={doMove} data-testid="knowledge-move-save">Flyt</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note view dialog */}
      <Dialog open={!!noteView} onOpenChange={(o) => !o && setNoteView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="knowledge-note-view">
          <DialogHeader><DialogTitle className="font-display">{noteView?.original_filename}</DialogTitle></DialogHeader>
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">{noteView?.note || noteView?.summary}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
